import { useState, useRef } from "react";
import { ReservationFormData } from "../types";
import { getCoordinates, getRouteDistance } from "@/lib/api";
import { estimateReservationPrice } from "@/lib/pricing/estimateReservationPrice";

/**
 * Estimation de prix : cache de coordonnées + cache de prix (persistants entre
 * les renders via refs), calcul déterministe avec mise à jour du breakdown dans
 * `formData`.
 *
 * Extrait tel quel de `useReservationForm` (aucun changement de comportement).
 */
export const usePriceEstimate = (
  formData: ReservationFormData,
  setFormData: React.Dispatch<React.SetStateAction<ReservationFormData>>,
) => {
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);

  // Caches persistants entre les renders
  const coordinatesCacheRef = useRef(
    new Map<string, { lat: number; lon: number }>(),
  );
  const priceCacheRef = useRef(new Map<string, number>());

  const getCachedCoordinates = async (address: string) => {
    if (coordinatesCacheRef.current.has(address)) {
      return coordinatesCacheRef.current.get(address) ?? null;
    }
    const coords = await getCoordinates(address);
    if (coords) {
      coordinatesCacheRef.current.set(address, coords);
    }
    return coords;
  };

  const generatePriceCacheKey = (formData: ReservationFormData): string => {
    return JSON.stringify({
      departure: formData.departure,
      arrival: formData.arrival,
      tripType: formData.tripType,
      stops: formData.stops,
      dispositionDuration: formData.dispositionDuration,
      selectedVehicleId: formData.selectedVehicleId,
    });
  };

  const calculatePrice = async (): Promise<{
    amount: number | null;
    error: string | null;
  }> => {
    setIsCalculatingPrice(true);
    setPriceError(null);

    try {
      const cacheKey = generatePriceCacheKey(formData);
      if (priceCacheRef.current.has(cacheKey)) {
        const cachedPrice = priceCacheRef.current.get(cacheKey)!;
        setEstimatedPrice(cachedPrice);
        setIsCalculatingPrice(false);
        return { amount: cachedPrice, error: null };
      }

      const pricingResult = await estimateReservationPrice(
        {
          departure: formData.departure,
          arrival: formData.arrival || undefined,
          tripType: formData.tripType,
          stops: formData.stops,
          dispositionDuration: formData.dispositionDuration,
          selectedVehicleId: formData.selectedVehicleId,
          passengers: formData.passengers,
          luggage: formData.luggage,
          babySeats: formData.babySeats,
          childSeats: formData.childSeats,
          acceptAnimals: formData.acceptAnimals,
        },
        {
          resolveCoordinates: getCachedCoordinates,
          resolveRouteDistance: async (start, end) =>
            getRouteDistance(
              { lat: start.lat, lon: start.lon },
              { lat: end.lat, lon: end.lon },
            ),
        },
      );

      setFormData((prev) => ({
        ...prev,
        priceBreakdown: pricingResult.priceBreakdown,
        dispositionDuration:
          pricingResult.normalizedInput.dispositionDuration ??
          prev.dispositionDuration,
      }));

      setEstimatedPrice(pricingResult.amount);
      priceCacheRef.current.set(cacheKey, pricingResult.amount);
      return { amount: pricingResult.amount, error: null };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Une erreur est survenue";
      setPriceError(errorMessage);
      return { amount: null, error: errorMessage };
    } finally {
      setIsCalculatingPrice(false);
    }
  };

  const resetPriceCache = () => {
    priceCacheRef.current.clear();
    setEstimatedPrice(null);
  };

  return {
    estimatedPrice,
    setEstimatedPrice,
    isCalculatingPrice,
    setIsCalculatingPrice,
    priceError,
    setPriceError,
    coordinatesCacheRef,
    priceCacheRef,
    getCachedCoordinates,
    calculatePrice,
    resetPriceCache,
  };
};
